import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ToolPageShell, StatusPill } from "@/components/AdminTools";
import ScreenerPreview from "@/components/ScreenerPreview";
import { Rocket, Trash2, Copy, ExternalLink, ListChecks, Inbox, ChevronRight, Eye } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function PortalScreeners() {
  const { user } = useAuth();
  const [screeners, setScreeners] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [activeDeployment, setActiveDeployment] = useState(null);
  const [customLabel, setCustomLabel] = useState({});
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    try {
      const [s, d, sub] = await Promise.all([
        api.get("/screeners"),
        api.get("/screener-deployments"),
        api.get("/screener-submissions"),
      ]);
      setScreeners((s.data || []).filter((x) => x.status === "active"));
      setDeployments(d.data || []);
      setSubmissions(sub.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const deploy = async (screener) => {
    try {
      await api.post("/screener-deployments", {
        screener_id: screener.id,
        custom_label: customLabel[screener.id] || null,
      });
      toast.success(`Deployed: ${screener.name}`);
      setCustomLabel((m) => ({ ...m, [screener.id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeDeployment = async (d) => {
    if (!window.confirm(`Stop sharing the "${d.custom_label || d.slug}" screener?`)) return;
    try {
      await api.delete(`/screener-deployments/${d.id}`);
      toast.success("Deployment removed.");
      if (activeDeployment === d.id) setActiveDeployment(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/s/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const filteredSubmissions = activeDeployment
    ? submissions.filter((s) => s.deployment_id === activeDeployment)
    : submissions;

  const screenerMap = Object.fromEntries(screeners.map((s) => [s.id, s]));

  return (
    <ToolPageShell
      eyebrow="Clinic · Lead capture"
      title="Screeners & deployments"
      intro="Deploy admin-authored screeners as branded landing pages. Submissions auto-route into your consumer pipeline."
    >
      {/* Available templates */}
      <section className="mb-10">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Available screener templates</h2>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="portal-screener-templates">
          {screeners.map((s) => (
            <div key={s.id} data-testid={`portal-screener-template-${s.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 capitalize">{s.category}</div>
                  <h3 className="mt-1 font-display text-base font-semibold text-slate-900">{s.name}</h3>
                  <p className="mt-1 text-xs text-slate-600 line-clamp-2">{s.intro}</p>
                </div>
                <ListChecks className="w-5 h-5 text-slate-300" />
              </div>
              <div className="mt-2 text-[11px] text-slate-500">{s.questions?.length || 0} questions</div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewing(s)}
                  data-testid={`portal-screener-preview-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 mr-1"
                >
                  <Eye className="w-3 h-3" /> Preview
                </button>
                <input
                  data-testid={`portal-screener-label-${s.id}`}
                  value={customLabel[s.id] || ""}
                  onChange={(e) => setCustomLabel((m) => ({ ...m, [s.id]: e.target.value }))}
                  placeholder="Custom label (optional)"
                  className="flex-1 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs"
                />
                <button
                  onClick={() => deploy(s)}
                  data-testid={`portal-screener-deploy-${s.id}`}
                  className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-800"
                >
                  <Rocket className="w-3.5 h-3.5" /> Deploy
                </button>
              </div>
            </div>
          ))}
          {screeners.length === 0 && (
            <div className="sm:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
              No active screener templates yet. Ask your concierge to enable some.
            </div>
          )}
        </div>
      </section>

      {/* Active deployments */}
      <section className="mb-10">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-900">Active deployments</h2>
          <span className="text-xs text-slate-500">{deployments.length} total</span>
        </div>

        <div className="space-y-3" data-testid="portal-deployments-list">
          {deployments.map((d) => {
            const tmpl = screenerMap[d.screener_id];
            const url = `${window.location.origin}/s/${d.slug}`;
            return (
              <div key={d.id} data-testid={`portal-deployment-${d.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-slate-900">
                      {d.custom_label || tmpl?.name || "Screener"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {tmpl?.name || d.screener_id} · created {fmt(d.created_at)}
                    </div>
                    <code className="mt-2 block text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700 break-all">
                      {url}
                    </code>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                      {d.submission_count || 0} submissions
                    </span>
                    <StatusPill status={d.status} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => copyLink(d.slug)}
                    data-testid={`portal-deployment-copy-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    <Copy className="w-3 h-3" /> Copy link
                  </button>
                  <a
                    href={`/s/${d.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`portal-deployment-preview-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                  >
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                  <button
                    onClick={() => setActiveDeployment(activeDeployment === d.id ? null : d.id)}
                    data-testid={`portal-deployment-submissions-${d.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border ${
                      activeDeployment === d.id
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Inbox className="w-3 h-3" /> View submissions
                    <ChevronRight className={`w-3 h-3 transition-transform ${activeDeployment === d.id ? "rotate-90" : ""}`} />
                  </button>
                  <button
                    onClick={() => removeDeployment(d)}
                    data-testid={`portal-deployment-remove-${d.id}`}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {deployments.length === 0 && (
            <div className="text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
              No deployed screeners yet. Deploy a template above to get a shareable link.
            </div>
          )}
        </div>
      </section>

      {/* Submissions */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Submissions
            {activeDeployment && (
              <span className="ml-2 text-xs font-normal text-slate-500">filtered</span>
            )}
          </h2>
          {activeDeployment && (
            <button
              onClick={() => setActiveDeployment(null)}
              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
            >
              Show all
            </button>
          )}
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="portal-submissions-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Consumer</th>
                <th className="text-left px-4 py-3">Screener</th>
                <th className="text-left px-4 py-3">Recommended packages</th>
                <th className="text-left px-4 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSubmissions.map((s) => (
                <tr key={s.id} data-testid={`portal-submission-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-display font-semibold text-slate-900">{s.consumer_name}</div>
                    <div className="text-[11px] text-slate-500">{s.consumer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{screenerMap[s.screener_id]?.name || s.screener_id}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {(s.recommended_package_ids || []).length || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmt(s.created_at)}</td>
                </tr>
              ))}
              {filteredSubmissions.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-500 py-10">
                    No submissions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {previewing && (
        <ScreenerPreview
          screener={previewing}
          clinicName={user?.clinic_name || user?.name}
          onClose={() => setPreviewing(null)}
        />
      )}
    </ToolPageShell>
  );
}
